import React, { useContext, useEffect, useRef } from 'react';
import {
    TyreRepairRequest,
    TyreDamageTypeOption,
    TyreDamageTypes,
    TyreSpecificErrors
} from './TyreRepairRequestTypes';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import SelectInput from '../../common/selectInput/SelectInput';
import FormInput from '../../common/FormInput';
import { t } from 'i18next';
import { TranslationHelper } from 'classes/TranslationHelper';
import DateInput from '../../common/dateInput/DateInput';
import { TyreRepairRequestContext } from '../../../context/TyreRepairRequestContext';

export type TyreSpecificFormProps = {
    repairRequest: TyreRepairRequest;
    formErrors?: TyreSpecificErrors;
    onChange: (key: string, value: string) => void;
    onChangeDamageType: (selectionName: string) => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    errorSummaryTitle: string;
};

const TyreSpecificForm = ({
    repairRequest,
    formErrors,
    onChange,
    errorSummaryTitle
}: TyreSpecificFormProps): JSX.Element => {
    const { tyreRepairRequest, onChangDateOfIncident } = useContext(
        TyreRepairRequestContext
    );

    const onChangeEvent = (selectedValue: string, key: string) => {
        onChange(key, selectedValue);
    };

    const modalBottom = useRef(null);

    useEffect(() => {
        if (formErrors !== undefined && Object.keys(formErrors).length !== 0) {
            modalBottom.current.focus();
        }
    }, [formErrors]);

    const tyreDamageTypeOptions: TyreDamageTypeOption[] = [
        {
            id: TyreDamageTypes.FlatTyre,
            text: TranslationHelper.getTranslatedText(TyreDamageTypes.FlatTyre),
            value: TyreDamageTypes.FlatTyre,
            disabled: false
        },
        {
            id: TyreDamageTypes.BlowOut,
            text: TranslationHelper.getTranslatedText(TyreDamageTypes.BlowOut),
            value: TyreDamageTypes.BlowOut,
            disabled: false
        }
    ];

    const maxDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate());
        return dt;
    };

    const errorlist = (): Array<string | undefined> => {
        const values = [formErrors?.damagedTyre];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    const onChangeDate = (selectedIncidentDate: string): void => {
        if (selectedIncidentDate !== null) {
            onChangDateOfIncident(selectedIncidentDate);
        }
    };

    return (
        <div className='card page-content no-border'>
            <div className='card-body page-content-body'>
                <FormInput
                    id='incident-date-forminput'
                    errorText={formErrors?.dateOfIncident}
                    label={t('incident_date')}>
                    <DateInput
                        name='dateOfIncidentDatePicker'
                        useJson
                        forceTimeReset
                        value={tyreRepairRequest?.dateOfIncident}
                        onDateChanged={(dateString) => onChangeDate(dateString)}
                        maximumDate={maxDate()}
                    />
                </FormInput>
                <FormInput
                    id='damage-type-forminput'
                    label={t('tyre_repair_request.step_2_damage_type_title')}
                    helpText={t('help_text.size_of_damage')}
                    errorText={formErrors?.damageType}>
                    <SelectInput
                        name='damage-size'
                        loadingText='Loading...'
                        value={repairRequest?.damageType}
                        options={tyreDamageTypeOptions}
                        onChange={(selectedValue: string) =>
                            onChangeEvent(selectedValue, 'damageType')
                        }
                        forceDefault
                        defaultIfOneOption
                        addDefaultOption
                        defaultText={t('select_default')}
                    />
                </FormInput>
            </div>
            <div className='card-footer page-content-footer'>
                {formErrors && (
                    <ErrorSummary
                        title={errorSummaryTitle}
                        errors={errorlist()}
                        className='card-error'
                    />
                )}
            </div>
            <div tabIndex={0} ref={modalBottom}></div>
        </div>
    );
};

export default TyreSpecificForm;
